import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { trackInstallPrompt } from '../utils/analytics';

/**
 * Convite flutuante para instalar o Astralyz como app (PWA).
 * Aparece em todas as páginas. Some se o app já está instalado
 * (rodando em standalone) ou se o usuário dispensou nos últimos 30 dias.
 * Ver hook `useInstallPrompt`.
 */

/** Glifo "Compartilhar" do iOS (quadrado com seta pra cima). */
const IOSShareIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M12 15V3" />
    <path d="m8 7 4-4 4 4" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
);

const InstallPrompt: React.FC = () => {
  const { canShow, platform, promptInstall, dismiss } = useInstallPrompt();
  const [iosHelp, setIosHelp] = useState(false);
  const shownTracked = useRef(false);

  useEffect(() => {
    if (canShow && !shownTracked.current) {
      shownTracked.current = true;
      trackInstallPrompt('shown', platform || undefined);
    }
  }, [canShow, platform]);

  const handleInstall = async () => {
    if (platform === 'ios') {
      setIosHelp((v) => !v);
      trackInstallPrompt('ios_help', 'ios');
      return;
    }
    const outcome = await promptInstall();
    trackInstallPrompt(outcome, 'android');
  };

  const handleDismiss = () => {
    trackInstallPrompt('dismissed', platform || undefined);
    dismiss();
  };

  return (
    <AnimatePresence>
      {canShow && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md sm:inset-x-4 sm:bottom-4"
          role="dialog"
          aria-label="Instalar o aplicativo Astralyz"
        >
          <div className="rounded-2xl border border-mystical-400/30 bg-[#1e0a3c]/95 p-4 shadow-2xl backdrop-blur-md">
            <div className="flex items-start gap-3">
              <img
                src="/logo192.png"
                alt=""
                className="h-11 w-11 flex-shrink-0 rounded-xl"
                draggable={false}
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white">Instale o Astralyz</p>
                <p className="mt-0.5 text-sm text-purple-200">
                  Adicione à tela inicial e abra como um app, sem a barra do navegador.
                </p>
              </div>
              <button
                onClick={handleDismiss}
                aria-label="Agora não"
                className="flex-shrink-0 rounded-lg p-1 text-purple-300 transition hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <AnimatePresence initial={false}>
              {platform === 'ios' && iosHelp && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 space-y-2 rounded-xl bg-black/30 p-3 text-sm text-purple-100">
                    <p className="flex items-center gap-2">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-mystical-500/30 text-xs font-bold">1</span>
                      Toque no ícone
                      <IOSShareIcon className="h-4 w-4 text-mystical-300" />
                      <span className="font-medium">Compartilhar</span>
                      na barra do Safari.
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-mystical-500/30 text-xs font-bold">2</span>
                      Escolha <span className="font-medium">"Adicionar à Tela de Início"</span>.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-3 flex gap-2">
              <button
                onClick={handleInstall}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-mystical-500 to-mystical-700 px-4 py-2.5 font-semibold text-white transition hover:brightness-110 active:scale-[0.98]"
              >
                {platform === 'ios' ? (
                  <>
                    <IOSShareIcon className="h-4 w-4" />
                    Como instalar
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Instalar
                  </>
                )}
              </button>
              <button
                onClick={handleDismiss}
                className="rounded-xl px-4 py-2.5 text-sm text-purple-300 transition hover:bg-white/10 hover:text-white"
              >
                Agora não
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
